import { Box, Flex, Text, useBreakpointValue } from "@chakra-ui/react";
import { LoginContext } from "@context/loginContext";
import { OportunityContext } from "@context/oportunityContex";
import { UsuallyContext } from "@context/usuallyContex";
import { getFaseGroupVariants } from "@helpers/faseGroups";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { IDashboardOportunidade } from "src/@types/Home";
import { theme } from "src/styles/theme";

type Props = {
  data: IDashboardOportunidade[];
};

const COLORS = [
  "#2C488A",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#AF19FF",
  "#0088FE",
];

const getColor = (index: number, total: number) => {
  if (index < COLORS.length) return COLORS[index];

  // Para índices além da paleta fixa, gera cores extras
  // distribuídas no restante da roda de matizes, evitando
  // repetir os tons já usados nas cores fixas.
  const extraCount = total - COLORS.length;
  const extraIndex = index - COLORS.length;
  const hue = (extraIndex * 360) / extraCount;
  return `hsl(${hue}, 70%, 55%)`;
};

const CustomOportunityByStatus: React.FC<Props> = React.memo(({ data }) => {
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const { setPageSidebar } = React.useContext(LoginContext);
  const { setHashSalesSidebar } = React.useContext(UsuallyContext);
  const { setFaseMyOportunity, setFaseHomeFilter } =
    React.useContext(OportunityContext);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleFase = React.useCallback((fase: string) => {
    if (fase === "Análise de Crédito") return "Em Análise de Crédito";
    else return fase;
  }, []);

  const handleRedirect = React.useCallback(
    (fase: string) => {
      navigate("/oportunidades/minhas-oportunidades");
      setPageSidebar("Minhas oportunidades");
      localStorage.setItem("@option", "Minhas oportunidades");
      setHashSalesSidebar("MyOportunityComponent");

      const faseAjustada = handleFase(fase);
      // Expande a fase clicada em todas as variações reais do mesmo grupo
      // (mesma lógica usada nas colunas do Kanban), pra trazer todos os
      // registros correspondentes e não só os que batem com o texto exato.
      const fasesAgrupadas = getFaseGroupVariants(faseAjustada);

      setFaseMyOportunity(fasesAgrupadas);
      setFaseHomeFilter(fasesAgrupadas);
    },
    [
      navigate,
      setPageSidebar,
      setHashSalesSidebar,
      setFaseMyOportunity,
      setFaseHomeFilter,
      handleFase,
    ],
  );

  const onMouseEnter = React.useCallback((dataIndex: number) => {
    setActiveIndex(dataIndex);
  }, []);

  const onMouseLeave = React.useCallback(() => {
    setActiveIndex(null);
  }, []);

  const memoizedData = React.useMemo(() => data || [], [data]);
  const totalQuantidade = React.useMemo(
    () => memoizedData.reduce((acc, curr) => acc + curr.quantidade, 0),
    [memoizedData],
  );

  const CustomTooltip = React.useCallback(({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const { fase, quantidade } = payload[0].payload;
      return (
        <Box
          p={2}
          bg="white"
          boxShadow="md"
          borderRadius="md"
          border="1px solid #ddd"
          zIndex={2}
        >
          <Text fontWeight="bold" fontSize="sm">
            {fase}
          </Text>
          <Text fontSize="sm">{`Quantidade: ${quantidade}`}</Text>
        </Box>
      );
    }
    return null;
  }, []);

  if (memoizedData.length === 0) {
    return (
      <Flex align="center" justify="center" h={"190px"}>
        <Text
          w={"fit-content"}
          color={theme.text50}
          textAlign="center"
          fontSize={{ base: "sm", md: "md" }}
        >
          Não há oportunidades por status!
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      mt={{ base: 4, md: 10 }}
      flexDir={{ base: "column", md: "row" }}
      gap={{ base: 3, md: 0 }}
    >
      {/* PieChart Responsivo */}
      <Box
        position="relative"
        w={{ base: "160px", sm: "180px", md: "40%" }}
        h={{ base: "120px", sm: "140px", md: "190px" }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={memoizedData}
              dataKey="quantidade"
              nameKey="fase"
              cx="50%"
              cy="50%"
              innerRadius={isMobile ? 45 : 80}
              outerRadius={isMobile ? 55 : 90}
              paddingAngle={0}
              onMouseEnter={(_, index) => onMouseEnter(index)}
              onMouseLeave={onMouseLeave}
            >
              {memoizedData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getColor(index, memoizedData.length)}
                  stroke={activeIndex === index ? theme.primary : "none"}
                  strokeWidth={activeIndex === index ? 2 : 1}
                />
              ))}
            </Pie>
            <Tooltip wrapperStyle={{ zIndex: 10 }} content={CustomTooltip} />
          </PieChart>
        </ResponsiveContainer>

        {/* Número centralizado */}
        <Box
          display={"flex"}
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          fontSize={{ base: "lg", sm: "xl", md: "2rem" }}
          fontWeight="semibold"
          color={theme.text}
          w={"fit-content"}
          zIndex={1}
        >
          {totalQuantidade}
        </Box>
      </Box>

      {/* Legenda */}
      <Box
        display={"flex"}
        flexDir={"column"}
        w={{ base: "100%", md: "70%" }}
        h={{ base: "100px", md: "150px" }}
        flexWrap={{ base: "wrap", sm: "wrap", md: "wrap" }}
        gap={{ base: "4px", md: "6px" }}
        overflowX={"hidden"}
        px={{ base: 2, md: 0 }}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
            height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#D3D3D3",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#A0AEC0",
          },
          "&::-webkit-scrollbar-track": {
            background: "#F7FAFC",
          },
        }}
      >
        {memoizedData.map(({ fase, quantidade }, index) => (
          <Flex
            key={index}
            align="center"
            mb={{ base: 1, md: 2 }}
            w={{ base: "fit-content", sm: "fit-content", md: "50%" }}
          >
            <Box
              w={{ base: 2, md: 3 }}
              h={{ base: 2, md: 3 }}
              borderRadius="full"
              bg={getColor(index, memoizedData.length)}
              mr={{ base: 2, md: 3 }}
              flexShrink={0}
            />
            <Text
              fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
              w={"fit-content"}
              color={theme.text}
              cursor={"pointer"}
              _hover={{
                textDecoration: "underline",
              }}
              onClick={() => handleRedirect(fase)}
            >
              {fase}
            </Text>
          </Flex>
        ))}
      </Box>
    </Flex>
  );
});

export default CustomOportunityByStatus;
